import React, { useState, useRef, useEffect } from 'react';
import { createWorker } from 'tesseract.js';
import { Account } from '../types';
import api from '../utils/api';
import CategorySelect from './CategorySelect';
import { Analytics } from '../utils/analytics';

interface ReceiptData {
  amount: string;
  merchant: string;
  date: string;
  description: string;
}

interface MerchantMapping {
  id: string;
  merchantName: string;
  category: string;
}

interface ReceiptScannerProps {
  accounts?: Account[];
  onSave?: (data: { accountId: string; amount: string; category: string; description: string; date: string }) => void;
  onClose: () => void;
}

const ReceiptScanner: React.FC<ReceiptScannerProps> = ({ accounts: providedAccounts, onSave, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [scannedText, setScannedText] = useState<string>('');
  const [extractedData, setExtractedData] = useState<ReceiptData | null>(null);
  const [merchantMappings, setMerchantMappings] = useState<MerchantMapping[]>([]);
  const [accounts, setAccounts] = useState<Account[]>(providedAccounts || []);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(!providedAccounts);
  const [accountsError, setAccountsError] = useState<string>('');
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [formData, setFormData] = useState({
    accountId: '',
    amount: '',
    category: 'FOOD',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Accounts laden, falls nicht übergeben
  useEffect(() => {
    const loadAccounts = async () => {
      if (!providedAccounts || providedAccounts.length === 0) {
        setIsLoadingAccounts(true);
        setAccountsError('');
        try {
          console.log('📊 Lade Konten...');
          const response = await api.get('/accounts');
          console.log('📊 Response:', response);
          
          // Debug-Info für UI
          setDebugInfo(`Response Type: ${typeof response.data}, IsArray: ${Array.isArray(response.data)}, Data: ${JSON.stringify(response.data).substring(0, 200)}`);
          
          let accountsData: Account[] = [];
          
          // Verschiedene Response-Strukturen unterstützen
          if (Array.isArray(response.data)) {
            // Direkt ein Array
            accountsData = response.data;
          } else if (response.data?.data?.accounts && Array.isArray(response.data.data.accounts)) {
            // Nested: { success: true, data: { accounts: [...] } }
            accountsData = response.data.data.accounts;
          } else if (response.data?.accounts && Array.isArray(response.data.accounts)) {
            // { accounts: [...] }
            accountsData = response.data.accounts;
          } else if (Array.isArray(response)) {
            // Response selbst ist Array
            accountsData = response;
          }
          
          if (accountsData.length > 0) {
            setAccounts(accountsData);
          } else {
            setAccountsError('Keine Konten gefunden. Bitte erstelle zuerst ein Konto.');
          }
        } catch (error: any) {
          console.error('❌ Fehler beim Laden der Konten:', error);
          setAccountsError(error.response?.data?.message || 'Fehler beim Laden der Konten');
        } finally {
          setIsLoadingAccounts(false);
        }
      } else {
        console.log('📊 Verwende übergebene Konten:', providedAccounts);
        setIsLoadingAccounts(false);
      }
    };
    
    loadAccounts();
  }, [providedAccounts]);

  // Merchant-Mappings beim Laden abrufen
  useEffect(() => {
    const loadMerchantMappings = async () => {
      try {
        const response = await api.get('/merchants');
        if (response.data) {
          setMerchantMappings(response.data);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Händler-Zuordnungen:', error);
      }
    };
    
    loadMerchantMappings();
  }, []);

  // Smart-Vorschläge für Kategorie basierend auf Händler
  const suggestCategory = (merchant: string): string => {
    const merchantUpper = merchant.toUpperCase();
    
    // 1. Prüfe User-spezifische Zuordnungen (höchste Priorität)
    for (const mapping of merchantMappings) {
      if (merchantUpper.includes(mapping.merchantName)) {
        return mapping.category;
      }
    }
    
    // 2. Fallback zu Standard-Zuordnungen
    if (merchantUpper.includes('REWE') || merchantUpper.includes('EDEKA') || 
        merchantUpper.includes('ALDI') || merchantUpper.includes('LIDL') ||
        merchantUpper.includes('PENNY') || merchantUpper.includes('NETTO') ||
        merchantUpper.includes('KAUFLAND')) {
      return 'FOOD';
    }
    if (merchantUpper.includes('SHELL') || merchantUpper.includes('ARAL') ||
        merchantUpper.includes('ESSO') || merchantUpper.includes('TOTAL')) {
      return 'TRANSPORT';
    }
    if (merchantUpper.includes('DM') || merchantUpper.includes('ROSSMANN') ||
        merchantUpper.includes('MÜLLER')) {
      return 'HEALTHCARE';
    }
    if (merchantUpper.includes('H&M') || merchantUpper.includes('ZARA') ||
        merchantUpper.includes('C&A')) {
      return 'CLOTHING';
    }
    if (merchantUpper.includes('SATURN') || merchantUpper.includes('MEDIA MARKT')) {
      return 'ELECTRONICS';
    }
    
    return 'EXPENSE';
  };

  const handleSave = async () => {
    if (!formData.accountId || !formData.amount || !formData.description) {
      alert('Bitte fülle mindestens Konto, Betrag und Beschreibung aus.');
      return;
    }
    
    // Speichere letztes genutztes Konto
    localStorage.setItem('lastUsedAccountId', formData.accountId);
    
    // Wenn onSave nicht übergeben wurde, direkt speichern
    if (!onSave) {
      try {
        await api.post('/transactions', {
          accountId: formData.accountId,
          type: 'EXPENSE',
          amount: parseFloat(formData.amount),
          category: formData.category,
          description: formData.description,
          date: formData.date
        });
        Analytics.receiptScanned(true);
        Analytics.transactionCreated(formData.category, parseFloat(formData.amount));
        alert('Transaktion erfolgreich gespeichert!');
        onClose();
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        Analytics.receiptScanned(false);
        alert('Fehler beim Speichern der Transaktion');
      }
    } else {
      onSave(formData);
      onClose();
    }
  };

  const parseReceiptText = (text: string): ReceiptData => {
    // ZUERST nach Zeilen splitten
    const rawLines = text.split('\n');
    
    // DANN jede Zeile bereinigen (Trennstriche entfernen)
    const lines = rawLines
      .map(line => line.replace(/[-_=]{3,}/g, ' ').trim()) // Trennstriche in Zeile entfernen
      .filter(line => line.length > 0); // Leere Zeilen entfernen
    
    console.log('\n=== OCR ANALYSE START ===');
    console.log('Anzahl erkannte Zeilen:', lines.length);
    console.log('Erste 10 Zeilen:', lines.slice(0, 10));
    console.log('Letzte 5 Zeilen:', lines.slice(-5));
    
    // Text bereinigen: häufige OCR-Fehler korrigieren
    const cleanedLines = lines.map(line => 
      line
        .replace(/\s+/g, ' ')  // Mehrfach-Leerzeichen entfernen
        .trim()
    );
    
    // Betrag finden - SUMME hat höchste Priorität!
    let amount = '';
    
    // 1. PRIORITÄT: Explizite SUMME/TOTAL/GESAMT Zeile
    const summePatterns = [
      /(?:summe|total|gesamt|zu\s*zahlen|betrag|bar|gesamt-betrag)[\s:]*[€]?\s*(\d+)[.,](\d{1,2})/i,
      /(?:summe|total|gesamt)[\s:]*[€]?\s*(\d+)\s*€/i, // SUMME ohne Komma aber mit €
    ];
    
    for (const pattern of summePatterns) {
      for (const line of cleanedLines) {
        const match = line.match(pattern);
        if (match) {
          // Betrag zusammensetzen
          if (match[2]) {
            // Hat Nachkommastellen
            const cents = match[2].padEnd(2, '0').substring(0, 2);
            amount = `${match[1]}.${cents}`;
          } else {
            // Nur Hauptbetrag (z.B. "SUMME 108€" -> vermutlich 1.08)
            const num = match[1];
            // Wenn > 99 ohne Komma, vermutlich fehlendes Komma
            if (parseInt(num) > 99 && num.length >= 3) {
              amount = `${num.slice(0, -2)}.${num.slice(-2)}`;
            } else {
              amount = `${num}.00`;
            }
          }
          console.log('✅ SUMME-Betrag gefunden:', amount, 'in Zeile:', line);
          break;
        }
      }
      if (amount) break;
    }
    
    // 2. Wenn keine SUMME gefunden: Betrag leer lassen
    if (!amount) {
      console.log('⚠️ Keine SUMME gefunden - Betrag muss manuell eingegeben werden');
    }

    // Händler (meist erste Zeilen oder bekannte Marken)
    let merchant = '';
    const knownMerchants = [
      // Supermärkte mit Varianten
      { pattern: /ALDI\s*(SÜD|NORD|SUD)?/i, name: 'ALDI' },
      { pattern: /REWE/i, name: 'REWE' },
      { pattern: /EDEKA/i, name: 'EDEKA' },
      { pattern: /LIDL/i, name: 'LIDL' },
      { pattern: /PENNY/i, name: 'PENNY' },
      { pattern: /NETTO/i, name: 'NETTO' },
      { pattern: /KAUFLAND/i, name: 'KAUFLAND' },
      // Fast Food
      { pattern: /MC\s*DONALD'?S?/i, name: 'MCDONALDS' },
      { pattern: /BURGER\s*KING/i, name: 'BURGER KING' },
      { pattern: /KFC/i, name: 'KFC' },
      { pattern: /STARBUCKS/i, name: 'STARBUCKS' },
      // Drogerie
      { pattern: /ROSSMANN/i, name: 'ROSSMANN' },
      { pattern: /DM[\s-]?(DROGERIE)?/i, name: 'DM' },
      { pattern: /MÜLLER/i, name: 'MÜLLER' },
      // Elektronik
      { pattern: /SATURN/i, name: 'SATURN' },
      { pattern: /MEDIA\s*MARKT/i, name: 'MEDIA MARKT' },
      // Andere
      { pattern: /IKEA/i, name: 'IKEA' },
      { pattern: /H\s*&\s*M/i, name: 'H&M' },
      { pattern: /C\s*&\s*A/i, name: 'C&A' },
      { pattern: /ZARA/i, name: 'ZARA' },
      { pattern: /DECATHLON/i, name: 'DECATHLON' },
      { pattern: /BAUHAUS/i, name: 'BAUHAUS' },
      // Tankstellen
      { pattern: /SHELL/i, name: 'SHELL' },
      { pattern: /ARAL/i, name: 'ARAL' },
      { pattern: /ESSO/i, name: 'ESSO' },
      { pattern: /TOTAL/i, name: 'TOTAL' },
      { pattern: /JET/i, name: 'JET' }
    ];
    
    // Erst bekannte Händler suchen - durchsuche ALLE Zeilen (Logos werden nicht erkannt, aber oft steht der Name im Text)
    for (const line of cleanedLines) {
      for (const known of knownMerchants) {
        if (known.pattern.test(line)) {
          merchant = known.name;
          console.log('✅ Bekannter Händler gefunden:', merchant, 'in Zeile:', line);
          break;
        }
      }
      if (merchant) break;
    }
    
    // Wenn kein bekannter Händler, nimm erste aussagekräftige Zeile
    // ABER: Filtere NUR reine generische Begriffe (nicht "Musterkaufhaus")
    if (!merchant) {
      const genericWords = /^(filiale|markt|laden|geschäft|shop|store|supermarkt)$/i;
      for (const line of cleanedLines.slice(0, 5)) {
        // Mindestens 3 Zeichen, maximal 50, keine reine Zahl, keine generischen Begriffe, kein Datum
        if (line.length > 3 && 
            line.length < 50 && 
            !/^\d+$/.test(line) &&
            !genericWords.test(line) &&
            !/^\d{2}[./]\d{2}/.test(line) &&
            !/^[\d\s,.-]+$/.test(line)) { // Keine reine Zahlen/Zeichen-Kombination
          merchant = line.trim();
          console.log('⚠️ Händler aus erster Zeile:', merchant);
          break;
        }
      }
    }
    
    // Fallback: "Unbekannter Händler" wenn nichts gefunden
    if (!merchant) {
      merchant = 'Unbekannter Händler';
      console.log('❌ Kein Händler erkannt');
    }

    // Datum finden - erweiterte Patterns (nimm das ERSTE gültige Datum)
    let date = new Date().toISOString().split('T')[0];
    let dateFound = false;
    
    const datePatterns = [
      /(\d{2})[./-](\d{2})[./-](\d{4})/,  // DD.MM.YYYY
      /(\d{2})[./-](\d{2})[./-](\d{2})/,  // DD.MM.YY
      /(\d{4})[./-](\d{2})[./-](\d{2})/   // YYYY.MM.DD
    ];
    
    for (const pattern of datePatterns) {
      if (dateFound) break;
      
      for (const line of lines) {
        const match = line.match(pattern);
        if (match) {
          let year, month, day;
          
          if (pattern === datePatterns[0]) {
            // DD.MM.YYYY
            day = match[1];
            month = match[2];
            year = match[3];
          } else if (pattern === datePatterns[1]) {
            // DD.MM.YY - 20XX annehmen
            day = match[1];
            month = match[2];
            const shortYear = parseInt(match[3]);
            year = shortYear > 50 ? `19${match[3]}` : `20${match[3]}`;
          } else {
            // YYYY.MM.DD
            year = match[1];
            month = match[2];
            day = match[3];
          }
          
          // Validiere Datum
          const parsedDate = new Date(`${year}-${month}-${day}`);
          if (!isNaN(parsedDate.getTime())) {
            date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            console.log('✅ Datum gefunden:', date, 'in Zeile:', line);
            dateFound = true;
            break;
          }
        }
      }
    }

    const result = {
      amount: amount ? `-${amount}` : '', // Negative für Ausgaben
      merchant: merchant.trim() || 'Unbekannter Händler',
      date,
      description: merchant.trim() ? `Einkauf - ${merchant.trim()}` : 'Kassenboneintrag'
    };
    
    console.log('=== PARSE ERGEBNIS ===');
    console.log('Händler:', result.merchant);
    console.log('Betrag:', result.amount);
    console.log('Datum:', result.date);
    console.log('=== OCR ANALYSE ENDE ===\n');
    return result;
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Bildvorschau
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // OCR starten
    setIsScanning(true);
    setProgress(0);
    setScannedText('');
    setExtractedData(null);

    try {
      const worker = await createWorker('deu', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        }
      });

      // Optimiere für Kassenbons (PSM 6 = uniform block, PSM 3 = auto)
      await worker.setParameters({
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜabcdefghijklmnopqrstuvwxyzäöüß€.,:-/&() ',
        tessedit_pageseg_mode: 3 as any, // Auto page segmentation
      });

      const { data } = await worker.recognize(file);
      console.log('OCR Rohdaten:', data.text);
      setScannedText(data.text);
      
      const extracted = parseReceiptText(data.text);
      setExtractedData(extracted);

      // Smart-Vorschläge: Formular vorausfüllen
      const suggestedCategory = suggestCategory(extracted.merchant);
      const lastUsedAccount = localStorage.getItem('lastUsedAccountId') || (accounts && accounts.length > 0 ? accounts[0].id : '');
      
      setFormData({
        accountId: lastUsedAccount,
        amount: extracted.amount,
        category: suggestedCategory,
        description: extracted.description,
        date: extracted.date
      });

      await worker.terminate();
    } catch (error) {
      console.error('OCR Fehler:', error);
      alert('Fehler beim Scannen des Belegs. Bitte versuchen Sie es erneut.');
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {isLoadingAccounts ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <p>Lade Konten...</p>
          </div>
        ) : accountsError ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>❌</div>
            <p style={{ color: '#dc2626', marginBottom: '10px', fontWeight: 'bold' }}>{accountsError}</p>
            {debugInfo && (
              <pre style={{ 
                fontSize: '10px', 
                background: '#f3f4f6', 
                padding: '10px', 
                borderRadius: '4px',
                textAlign: 'left',
                overflow: 'auto',
                marginBottom: '20px'
              }}>
                {debugInfo}
              </pre>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Schließen
            </button>
          </div>
        ) : (
          <>
            <h2 style={{ marginBottom: '20px' }}>📸 Beleg scannen</h2>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />

        {!previewImage && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '15px',
              fontSize: '16px',
              marginBottom: '10px'
            }}
          >
            📷 Foto aufnehmen / Bild auswählen
          </button>
        )}

        {previewImage && (
          <div style={{ marginBottom: '20px' }}>
            <img 
              src={previewImage} 
              alt="Beleg Vorschau" 
              style={{ 
                width: '100%', 
                borderRadius: '8px',
                border: '2px solid #e2e8f0'
              }} 
            />
          </div>
        )}

        {isScanning && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              background: '#f1f5f9', 
              borderRadius: '8px', 
              padding: '15px',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#64748b' }}>
                Scanne Beleg... {progress}%
              </p>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0f2027, #203a43, #2c5364)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          </div>
        )}

        {extractedData && (
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>
              ✨ Erkannte Daten - Bitte prüfen und anpassen
            </h3>
            
            {/* Konto */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Konto *
              </label>
              <select
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">Bitte wählen...</option>
                {accounts && accounts.length > 0 ? accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({acc.type})
                  </option>
                )) : (
                  <option value="" disabled>Lade Konten...</option>
                )}
              </select>
            </div>

            {/* Betrag */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Betrag (€) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: '#64748b', fontSize: '12px', marginTop: '3px', display: 'block' }}>
                Negative Werte für Ausgaben
              </small>
            </div>

            {/* Kategorie */}
            <div style={{ marginBottom: '15px' }}>
              <CategorySelect
                value={formData.category}
                onChange={(value) => setFormData({ ...formData, category: value })}
                required
              />
            </div>

            {/* Beschreibung */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Beschreibung *
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Datum */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Datum *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            {scannedText && (
              <details style={{ marginTop: '15px' }}>
                <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '13px' }}>
                  📄 Erkannter Text anzeigen
                </summary>
                <pre style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: 'white',
                  borderRadius: '6px',
                  fontSize: '11px',
                  maxHeight: '150px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {scannedText}
                </pre>
              </details>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
          {extractedData && (
            <button
              onClick={handleSave}
              className="btn btn-success"
              style={{ padding: '12px', fontSize: '15px' }}
            >
              ✓ Transaktion speichern
            </button>
          )}
          
          {previewImage && !isScanning && (
            <button
              onClick={() => {
                setPreviewImage(null);
                setScannedText('');
                setExtractedData(null);
                fileInputRef.current?.click();
              }}
              className="btn btn-secondary"
              style={{ padding: '12px', fontSize: '15px' }}
            >
              🔄 Neues Foto
            </button>
          )}

          <button
            onClick={onClose}
            className="btn btn-secondary"
            style={{ padding: '12px', fontSize: '15px' }}
          >
            Abbrechen
          </button>
        </div>

        <p style={{ 
          marginTop: '15px', 
          fontSize: '12px', 
          color: '#94a3b8',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          💡 <strong>Tipp für beste Ergebnisse:</strong><br/>
          • Fotografiere den Beleg gerade und gut beleuchtet<br/>
          • Vermeide Schatten und Reflexionen<br/>
          • Logo und Text sollten scharf sein<br/>
          • Die Kategorie wird automatisch vorgeschlagen! 🎯
        </p>
        </>
        )}
      </div>
    </div>
  );
};

export default ReceiptScanner;
